import { useReactiveVar, useQuery } from "@apollo/client";
import { useTranslation } from "next-i18next";
import { useMemo } from "react";
import clsx from "clsx";

import {
  LeaderboardType,
  Currency,
  GetLeaderboardRankingsDocument,
  GetMyRankingDocument,
} from "@/graphql/types";
import LoadingIndicator from "@/components/ui/svg/LoadingIndicator";
import { hasAccessTokenVar } from "@/apollo/links/auth";
import { getCurrencyCode } from "@/utils/currencies";
import LeaderboardPodium from "@/components/LeaderboardCard/Podium";
import LeaderboardEntry from "@/components/LeaderboardCard/Entry";

interface LeaderboardCardProps {
  leaderboard: LeaderboardType;
  title: string;
  subtitle: string;
}

function LeaderboardCard({ leaderboard, title, subtitle }: LeaderboardCardProps) {
  const currency = getCurrencyCode() as string as Currency;
  const isLogged = useReactiveVar(hasAccessTokenVar);
  const { t } = useTranslation("leaderboards");

  const { data: rankData } = useQuery(GetLeaderboardRankingsDocument, {
    variables: { limit: 9, leaderboard, currency },
    // fetchPolicy: "cache-and-network",
  });
  const { data: myRankData } = useQuery(GetMyRankingDocument, {
    variables: { leaderboard, currency },
    // fetchPolicy: "cache-and-network",
    skip: !isLogged,
  });

  const amInTop9 = useMemo(() => {
    return rankData?.leaderboardRankings.find((entry) => entry.user.id === myRankData?.me.id);
  }, [myRankData, rankData]);
  const myEntry = myRankData?.me.leaderboardEntry;
  const top3 = rankData?.leaderboardRankings.slice(0, 3) ?? [];
  const otherRanks = rankData?.leaderboardRankings.slice(3, isLogged && !amInTop9 ? 8 : 9) ?? [];
  return (
    <div
      className={clsx(
        "flex flex-col rounded-md p-5 lg:p-8 relative",
        "bg-[image:linear-gradient(139deg,#313F5E_-40%,rgba(32,35,50,0.00)_42%)]",
        "before:bg-purple-4.5/[0.07] before:border before:border-gradient before:rounded-md"
      )}
    >
      <div className="flex flex-col items-start max-w-[46ch] mb-14 lg:mb-15">
        <h2 className="text-22 leading-1.2 lg:text-28 lg:leading-1.2 font-medium text-purple">{title}</h2>
        <p className="text-sm leading-1.2 lg:text-base lg:leading-1.2 mt-1.5 opacity-60">{subtitle}</p>
      </div>

      <LeaderboardPodium entries={top3} />

      {otherRanks.length <= 0 && (
        <div className="flex flex-col items-center py-10">
          {!rankData && (
            <div className="text-purple-2">
              <LoadingIndicator width="24" height="24" />
            </div>
          )}
          {rankData && (
            <div className="w-full text-center text-sm lg:text-base">
              {t("leaderboards.none", { context: top3.length === 0 ? undefined : "else" })}
            </div>
          )}
        </div>
      )}

      {otherRanks.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-4 gap-x-6 mt-5 lg:mt-6">
          {isLogged && !amInTop9 && (
            <LeaderboardEntry
              key="mine"
              entry={myEntry}
              fallbackAvatarUrl={myRankData?.me.avatarUrl}
              forceMine
            />
          )}
          {otherRanks.map((entry) => (
            <LeaderboardEntry key={entry.user.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

export default LeaderboardCard;
